'use client';

import { useRef, useState } from 'react';
import { useIsomorphicLayoutEffect } from 'hooks/dom/useIsomorphicLayoutEffect';
import { debounce, throttle } from 'utils/performance/debounce';

const clamp = (value: number): number => Math.max(0, value);

const isBetween = (value: number, floor: number, ceil: number): boolean =>
  value >= floor && value <= ceil;

// hooks
const useScrollspy = (
  ids: string[],
  offset = 0,
  isScrollingRef?: React.MutableRefObject<boolean>,
  callbackRef?: React.MutableRefObject<(() => void) | null>,
): string => {
  const [activeId, setActiveId] = useState<string>('');
  const activeIdRef = useRef<string>('');

  useIsomorphicLayoutEffect(() => {
    const listener = () => {
      // Skip scrollspy updates during programmatic scrolling
      if (isScrollingRef?.current) return;

      const scroll = window.pageYOffset;

      const position = ids
        .map((id) => {
          const element = document.getElementById(id);

          if (!element) return { id, top: -1, bottom: -1 };

          const rect = element.getBoundingClientRect();
          const top = clamp(rect.top + scroll - offset);
          const bottom = clamp(rect.bottom + scroll - offset);

          return { id, top, bottom };
        })
        .find(({ top, bottom }) => isBetween(scroll, top, bottom));

      if (position && position.id && activeIdRef.current !== position.id) {
        activeIdRef.current = position.id;
        setActiveId(position.id);
      }
    };

    // Expose listener to parent component
    if (callbackRef) {
      callbackRef.current = listener;
    }

    listener();

    const throttledScroll = throttle(listener, 120);
    const debouncedResize = debounce(listener, 200);

    window.addEventListener('resize', debouncedResize);
    window.addEventListener('scroll', throttledScroll, { passive: true });

    return () => {
      window.removeEventListener('resize', debouncedResize);
      window.removeEventListener('scroll', throttledScroll as EventListener);
      if (callbackRef) {
        callbackRef.current = null;
      }
    };
  }, [ids, offset, isScrollingRef, callbackRef]);
  return activeId;
};

export default useScrollspy;
